import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CotMarketDto, CotParticipantDto, CotParticipantKeyDto } from 'shared';

import { Badge } from '../components/ui/Badge';
import { Sparkline } from '../components/ui/Sparkline';
import { useMarketCrowding } from '../features/marketConsole/queries';
import { useReplayDate } from '../lib/useReplayDate';
import { cn } from '../lib/utils';

const PARTICIPANTS: ReadonlyArray<{ key: CotParticipantKeyDto; label: string; note: string }> = [
  { key: 'largeSpeculators', label: 'Large speculators', note: 'Trend-following funds' },
  { key: 'commercials', label: 'Commercials', note: 'Producers and hedgers' },
  { key: 'smallTraders', label: 'Small traders', note: 'Non-reportable positions' },
];

function formatContracts(value: number): string {
  return `${value > 0 ? '+' : ''}${value.toLocaleString('en-US')}`;
}

function indexTone(value: number | null, threshold: number): string {
  if (value === null) return 'bg-text-subtle';
  if (value >= 100 - threshold || value <= threshold) return 'bg-warn';
  return 'bg-primary';
}

function ParticipantRow({
  label,
  note,
  participant,
  threshold,
}: {
  label: string;
  note: string;
  participant: CotParticipantDto;
  threshold: number;
}) {
  const index = participant.rangeIndex;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-text-muted">{note}</div>
      </div>
      <div className="text-right font-mono text-sm tabular-nums">
        <div>{index === null ? '—' : index.toFixed(0)}</div>
        <div className="text-[11px] text-text-muted">index</div>
      </div>
      <div
        className="col-span-2 h-2 overflow-hidden rounded-full bg-bg-muted"
        role="img"
        aria-label={`${label} positioning index ${index === null ? 'not available' : `${index.toFixed(0)} of 100`}`}
      >
        {index !== null ? (
          <div
            className={cn('h-full rounded-full', indexTone(index, threshold))}
            style={{ width: `${Math.max(index, 2)}%` }}
          />
        ) : null}
      </div>
      <div className="col-span-2 flex justify-between font-mono text-[11px] tabular-nums text-text-muted">
        <span>Net {formatContracts(participant.net)}</span>
        <span>
          {participant.netPctOpenInterest === null
            ? '—'
            : `${participant.netPctOpenInterest.toFixed(1)}% of OI`}
        </span>
      </div>
    </div>
  );
}

function CrowdingBadge({ crowding }: { crowding: CotMarketDto['crowding'] }) {
  if (crowding === 'CROWDED_LONG') return <Badge tone="warning">Speculators crowded long</Badge>;
  if (crowding === 'CROWDED_SHORT') return <Badge tone="warning">Speculators crowded short</Badge>;
  return <Badge tone="neutral">Not extreme</Badge>;
}

function MarketCard({ market, threshold }: { market: CotMarketDto; threshold: number }) {
  const sparkline = market.history.flatMap((point) =>
    point.largeSpeculators === null ? [] : [{ date: point.date, value: point.largeSpeculators }],
  );
  return (
    <article className="rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold">
            {market.name}{' '}
            <span className="text-sm font-normal text-text-muted">· {market.proxyEtf}</span>
          </h3>
          <div className="text-[11px] text-text-muted">
            Report {market.latestReportDate} · OI {market.openInterest.toLocaleString('en-US')}
          </div>
        </div>
        <CrowdingBadge crowding={market.crowding} />
      </div>
      {sparkline.length > 1 ? (
        <div className="mt-3 flex items-center gap-3">
          <span className="text-[11px] text-text-muted">Speculator index, 52 weeks</span>
          <Sparkline
            className="flex-1"
            data={sparkline}
            label={`${market.name} large speculator index over the last ${sparkline.length} weeks`}
          />
        </div>
      ) : null}
      <div className="mt-2 divide-y divide-border">
        {PARTICIPANTS.map((participant) => (
          <ParticipantRow
            key={participant.key}
            label={participant.label}
            note={participant.note}
            participant={market.participants[participant.key]}
            threshold={threshold}
          />
        ))}
      </div>
    </article>
  );
}

export function MarketCrowding() {
  const crowding = useMarketCrowding(useReplayDate().asOf);

  if (crowding.isLoading) {
    return <main className="p-6 text-text-muted">Loading positioning…</main>;
  }
  if (crowding.isError || !crowding.data) {
    return <main className="p-6 text-danger">Positioning data could not be loaded.</main>;
  }

  const data = crowding.data;
  const groups = [...new Set(data.markets.map((market) => market.group))];
  const extremes = data.markets.filter((market) => market.crowding !== null);
  const years = Math.round(data.lookbackWeeks / 52);

  return (
    <main className="text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Crowding</h1>
            <p className="mt-1 text-sm text-text-muted">
              Weekly futures positioning: where each group&apos;s net position sits in its {years}
              -year range.
            </p>
          </div>
          {data.markets[0] ? (
            <div className="text-xs text-text-muted">
              Report · {data.markets[0].latestReportDate} · Informational only
            </div>
          ) : null}
        </header>

        {data.status === 'NOT_AVAILABLE' ? (
          <section className="flex gap-3 rounded-lg border border-warn/30 bg-warn/10 p-4 text-sm text-warn">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>
              Positioning data has not been captured yet. Run the CFTC positioning refresh in{' '}
              <Link to="/settings/market-data" className="underline">
                Settings → Market data
              </Link>
              .
            </p>
          </section>
        ) : (
          <>
            <section className="rounded-lg border border-border bg-surface p-4 text-sm">
              <h2 className="font-semibold">How to read this</h2>
              <p className="mt-1 text-text-muted">
                The index runs from 0 (most net short in {data.lookbackWeeks} weeks) to 100 (most
                net long). Large speculators at or above {100 - data.extremeThreshold}, or at or
                below {data.extremeThreshold}, are flagged as crowded. Crowding says a move is well
                owned, not that it is over.
              </p>
              <p className="mt-2 font-medium">
                {extremes.length === 0
                  ? 'No tracked market is at a speculator extreme.'
                  : `${extremes.length} at a speculator extreme: ${extremes
                      .map((market) => market.name)
                      .join(', ')}.`}
              </p>
            </section>

            {groups.map((group) => (
              <section key={group} aria-labelledby={`crowding-${group}`}>
                <h2
                  id={`crowding-${group}`}
                  className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted"
                >
                  {group}
                </h2>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {data.markets
                    .filter((market) => market.group === group)
                    .map((market) => (
                      <MarketCard
                        key={market.code}
                        market={market}
                        threshold={data.extremeThreshold}
                      />
                    ))}
                </div>
              </section>
            ))}

            {data.unavailableMarkets.length > 0 ? (
              <p className="text-xs text-warn">
                No stored reports for: {data.unavailableMarkets.join(', ')}.
              </p>
            ) : null}
          </>
        )}

        <p className="text-xs text-text-muted">
          Data:{' '}
          <a href={data.source.url} target="_blank" rel="noopener noreferrer" className="underline">
            {data.source.name}
          </a>
          . Released weekly; positions are as of the prior Tuesday.
        </p>
      </div>
    </main>
  );
}
